import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Users, ChevronLeft, Calendar, KeyRound } from "lucide-react";
import { DailyHistoryTree } from "./History";
import { EmployeeDailySettings } from "../components/EmployeeDailySettings";

export default function EmployeeRecords() {
    const { t } = useLang();
    const { company } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const res = await api.get("/auth/company/employees");
            setData(res.data);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const code = company?.company_code || data?.company_code;
    const hasAccess = (company?.employee_limit || 0) > 0;

    return (
        <AppShell>
            <div className="mb-6 sm:mb-8">
                <Link to="/" className="text-sm font-medium text-neutral-600 hover:text-neutral-900">
                    ← {t.pickerLedgersTitle}
                </Link>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">
                    {t.employeeRecordsKicker}
                </p>
                <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">
                    {t.employeeRecordsTitle}
                </h1>
                <p className="mt-1 text-xs text-neutral-500 sm:text-sm">{t.employeeRecordsSubtitle}</p>
            </div>

            {/* Company code card */}
            <div
                className="mb-6 grid grid-cols-1 gap-3 rounded-2xl border border-amber-200 bg-amber-50/50 p-4 sm:grid-cols-2 sm:gap-4 sm:p-5"
                data-testid="employee-records-company-card"
            >
                <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700">
                        {t.managerCompanyCodeLabel}
                    </div>
                    <div className="mt-1 font-numbers text-2xl font-bold tracking-widest text-neutral-900" data-testid="manager-company-code">
                        {code || "—"}
                    </div>
                    <p className="mt-1 text-xs text-neutral-500">{t.managerCompanyCodeHint}</p>
                </div>
                <div className="flex flex-col gap-1">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700">
                        {t.managerEmployeeLimitLabel}
                    </div>
                    <div className="font-numbers text-2xl font-bold text-neutral-900">
                        {data?.employee_limit || 0}
                    </div>
                    {!hasAccess ? (
                        <Link
                            to="/settings"
                            className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900"
                            data-testid="setup-access-link"
                        >
                            <KeyRound className="h-3.5 w-3.5" />
                            {t.managerNoAccessSetup}
                        </Link>
                    ) : null}
                </div>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="h-32 animate-pulse rounded-2xl bg-neutral-200/60" />
                    ))}
                </div>
            ) : !data?.employees?.length ? (
                <div className="rounded-2xl border border-dashed border-neutral-300 bg-white/60 p-12 text-center" data-testid="employees-empty">
                    <Users className="mx-auto mb-3 h-8 w-8 text-neutral-400" />
                    <h3 className="font-heading text-lg font-bold">{t.employeeRecordsEmpty}</h3>
                    <p className="mt-1 text-sm text-neutral-500">{t.employeeRecordsEmptyHint}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="employees-list">
                    {data.employees.map((e) => (
                        <Link
                            key={e.id}
                            to={`/employees/${e.id}`}
                            className="group block rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                            data-testid={`employee-card-${e.id}`}
                        >
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="font-heading text-lg font-bold leading-tight">{e.company_name}</h3>
                                    <p className="mt-0.5 text-xs text-neutral-500">
                                        {t.employeeJoinedAt} {e.created_at ? new Date(e.created_at).toLocaleDateString("en-GB") : "—"}
                                    </p>
                                </div>
                                <ChevronLeft className="h-5 w-5 text-neutral-400 transition group-hover:-translate-x-1" />
                            </div>
                            <div className="mt-4 flex flex-wrap items-center gap-2">
                                <span className="inline-flex items-center gap-1.5 rounded-lg bg-sky-50 px-2.5 py-1.5 text-xs font-bold text-sky-700 ring-1 ring-sky-200">
                                    <Calendar className="h-3.5 w-3.5" />
                                    {t.employeeDailyCount}: <span className="font-numbers">{e.daily_count}</span>
                                </span>
                                <EmployeeDailySettings employee={e} onSaved={load} />
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </AppShell>
    );
}

export function EmployeeDailyList() {
    const { t, lang } = useLang();
    const { empId } = useParams();
    const [name, setName] = useState("");
    useEffect(() => {
        api.get(`/auth/company/employees/${empId}/ledgers/tree`).then((r) => setName(r.data?.employee?.company_name || "")).catch(() => {});
    }, [empId]);
    return <DailyHistoryTree t={t} lang={lang} empId={empId} title={name || t.dailyRecordsTitle} />;
}

function SummaryChip({ label, value }) {
    return (
        <div className="rounded-lg bg-neutral-50 px-2.5 py-1.5 ring-1 ring-neutral-100">
            <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-600">{label}</div>
            <div className="mt-0.5 font-numbers text-sm font-bold text-neutral-800">
                {Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 3 })}
            </div>
        </div>
    );
}
